if (!customElements.get('unlock-free-shipping-popup')) {
  class UnlockFreeShippingPopup extends HTMLElement {
    constructor() {
      super();

      this.config = this.parseConfig();
      this.modal = this.querySelector('[data-unlock-modal]');
      this.card = this.querySelector('[data-unlock-card]');
      this.track = this.querySelector('[data-unlock-track]');
      this.slides = [];
      this.acceptButton = this.querySelector('[data-unlock-accept]');
      this.skipButton = this.querySelector('[data-unlock-skip]');
      this.closeButton = this.querySelector('[data-unlock-close]');
      this.prevButton = this.querySelector('[data-unlock-prev]');
      this.nextButton = this.querySelector('[data-unlock-next]');
      this.dotsContainer = this.querySelector('[data-unlock-dots]');

      this.activeIndex = 0;
      this.activeOffer = null;
      this.previouslyFocused = null;
      this.isAdding = false;
      this.isPreview = false;
      this.scrollRaf = null;

      // Trigger (Mini Bloat OTP) line item, captured from the PDP add event so we can
      // re-add it before navigating away if it ever drops out of the cart.
      this.triggerVariantId = null;
      this.triggerQuantity = 1;

      this.handlePdpAdded = this.handlePdpAdded.bind(this);
      this.handleProductLinkClick = this.handleProductLinkClick.bind(this);
      this.handleAccept = this.handleAccept.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleClose = () => this.closeModal({ openCart: true });
      this.handlePrev = () => this.goToSlide(this.activeIndex - 1);
      this.handleNext = () => this.goToSlide(this.activeIndex + 1);
      this.handleTrackScroll = this.handleTrackScroll.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
    }

    connectedCallback() {
      // The carousel only needs featured products; the trigger product is required for the
      // storefront ATC flow but not for theme-editor previews.
      if (!this.config?.products?.length) {
        return;
      }

      this.slides = Array.from(this.querySelectorAll('[data-unlock-slide]'));
      this.buildOffersFromDom();

      if (this.config.triggerProductId) {
        document.addEventListener('arrae:pdp:added', this.handlePdpAdded);
      }

      this.acceptButton?.addEventListener('click', this.handleAccept);
      this.skipButton?.addEventListener('click', this.handleClose);
      this.closeButton?.addEventListener('click', this.handleClose);
      this.modal?.addEventListener('click', this.handleBackdropClick);
      this.prevButton?.addEventListener('click', this.handlePrev);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.track?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
      this.addEventListener('click', this.handleProductLinkClick);

      this.dotsContainer?.querySelectorAll('[data-unlock-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.unlockDot || 0);
          this.goToSlide(index);
        });
      });

      this.goToSlide(0, false);
      this.bindThemeEditorEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('arrae:pdp:added', this.handlePdpAdded);
      this.removeEventListener('click', this.handleProductLinkClick);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    bindThemeEditorEvents() {
      if (!window.Shopify?.designMode) return;

      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    handleSectionSelect(event) {
      const selectedSectionId = String(event?.detail?.sectionId || '');
      if (!selectedSectionId || selectedSectionId !== String(this.config?.sectionId || '')) return;
      if (!this.offers?.length) return;

      this.isPreview = true;
      this.goToSlide(this.activeIndex, false);
      this.openModal();
    }

    handleSectionDeselect(event) {
      const selectedSectionId = String(event?.detail?.sectionId || '');
      if (!selectedSectionId || selectedSectionId !== String(this.config?.sectionId || '')) return;
      if (!this.isPreview) return;

      this.isPreview = false;
      this.closeModal({ openCart: false });
    }

    parseConfig() {
      const configElement = this.querySelector('[data-unlock-config]');
      if (!configElement) return null;

      try {
        return JSON.parse(configElement.textContent);
      } catch (error) {
        window.console.error('[unlock-free-shipping-popup] Invalid config JSON.', error);
        return null;
      }
    }

    buildOffersFromDom() {
      this.offers = this.slides.map((slide) => {
        const productId = Number(slide.dataset.productId || 0);
        const product = this.config.products.find((item) => Number(item.id) === productId);
        const variants = this.filterSubscriptionVariants(product?.variants || []);
        const selectedVariant = this.pickInitialVariant(variants);

        return {
          slide,
          product,
          variants,
          selectedVariant,
        };
      }).filter((offer) => offer.product && offer.selectedVariant);
    }

    filterSubscriptionVariants(variants) {
      if (!Array.isArray(variants)) return [];

      const subscriptionVariants = variants.filter((variant) => {
        const allocations = variant?.selling_plan_allocations || [];
        if (!allocations.length || !variant.available) return false;

        const title = String(variant.title || '').toLowerCase();
        if (
          title.includes('one time') ||
          title.includes('1 time') ||
          title.includes('time purchase') ||
          title.includes('single')
        ) {
          return false;
        }

        return true;
      });

      if (subscriptionVariants.length) return subscriptionVariants;

      return variants.filter(
        (variant) => variant.available && (variant.selling_plan_allocations || []).length > 0
      );
    }

    pickInitialVariant(variants) {
      if (!variants?.length) return null;
      return variants.find((variant) => variant.available) || variants[0];
    }

    getCadenceFromSellingPlan(sellingPlan) {
      if (!sellingPlan) return null;

      const optionValues = (sellingPlan.options || []).map((option) => option.value).join(' ');
      const source = `${sellingPlan.name || ''} ${optionValues}`.toLowerCase();
      const matched = source.match(/(\d+)\s*(day|week|month|year)/i);

      return matched ? { interval: Number(matched[1]), unit: matched[2].toLowerCase() } : null;
    }

    getMonthlyAllocation(variant) {
      const allocations = variant?.selling_plan_allocations || [];
      if (!allocations.length) return null;

      // Prefer the 1-month delivery plan, then any monthly plan, then the first available.
      const everyMonth = allocations.find((allocation) => {
        const cadence = this.getCadenceFromSellingPlan(allocation.selling_plan);
        return cadence?.unit === 'month' && cadence.interval === 1;
      });
      if (everyMonth) return everyMonth;

      const anyMonth = allocations.find(
        (allocation) => this.getCadenceFromSellingPlan(allocation.selling_plan)?.unit === 'month'
      );

      return anyMonth || allocations[0];
    }

    getSellingPlanIdForVariant(variant) {
      const allocation = this.getMonthlyAllocation(variant);
      return allocation ? String(allocation.selling_plan_id) : null;
    }

    getPriceForVariant(variant) {
      const allocation = this.getMonthlyAllocation(variant);
      if (allocation?.price != null) return allocation.price;
      return variant.price;
    }

    getComparePriceForVariant(variant) {
      const allocation = this.getMonthlyAllocation(variant);
      if (allocation?.compare_at_price != null && allocation.compare_at_price > 0) {
        return allocation.compare_at_price;
      }
      return variant.price;
    }

    getPricingForVariant(variant) {
      const price = this.getPriceForVariant(variant);
      const compareAt = this.getComparePriceForVariant(variant);
      return {
        price,
        compareAt: compareAt > price ? compareAt : 0,
      };
    }

    formatMoney(cents) {
      const value = Number(cents || 0);
      const format = this.config.settings?.moneyFormat;

      if (window.Shopify?.formatMoney && format) {
        let formatted = window.Shopify.formatMoney(value, format);
        if (value % 100 === 0) {
          formatted = formatted.replace(/\.00(?!\d)/g, '').replace(/,00(?!\d)/g, '');
        }
        return formatted;
      }

      const amount = value / 100;
      const display = value % 100 === 0 ? String(Math.round(amount)) : amount.toFixed(2);
      return `$${display}`;
    }

    getDisplayLabel(variant) {
      return String(variant?.option1 || variant?.title || '').trim();
    }

    handlePdpAdded(event) {
      const payload = event.detail || {};
      const quantity = Number(payload.quantity ?? 1);

      if (
        !payload.productId ||
        Number(payload.productId) !== Number(this.config.triggerProductId) ||
        payload.sellingPlanId ||
        !Number.isFinite(quantity) ||
        quantity !== 1
      ) {
        return;
      }

      payload.deferCartDrawer = true;
      window.__pdpUpsellDeferCartDrawer = true;
      window.__pdpUpsellPopupPending = false;

      // Remember the trigger variant so we can re-add it if the user navigates away.
      if (payload.variantId) this.triggerVariantId = Number(payload.variantId);
      this.triggerQuantity = quantity;

      this.activeOffer = this.offers[this.activeIndex] || this.offers[0];
      if (!this.activeOffer) return;

      const discountCode = String(this.config.triggerDiscountCode || '').trim();

      // markTriggerAsGift must complete before applyDiscountCode — concurrent cart
      // mutations (cart/change.js + cart/update.js) race and can cause the attribution
      // property write to be lost, which breaks the "no duplicate gift" guard.
      this.markTriggerAsGift(payload)
        .catch((error) => {
          window.console.error('[unlock-free-shipping-popup]', error);
        })
        .then(() =>
          discountCode
            ? this.applyDiscountCode(discountCode).catch((error) => {
                window.console.error('[unlock-free-shipping-popup] discount:', error);
              })
            : Promise.resolve()
        )
        .finally(() => {
          this.closeCartDrawerIfOpen();
          this.goToSlide(this.activeIndex, false);
          this.openModal();
        });
    }

    getTriggerLineItemProperties(existingProperties = {}) {
      return {
        ...existingProperties,
        _mini_bloat_offer: 'true',
        _attribution: 'Unlock Free Shipping Gift',
      };
    }

    async markTriggerAsGift(payload) {
      if (!payload?.lineItemKey) return;

      const properties = this.getTriggerLineItemProperties(payload.item?.properties || {});

      await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: payload.lineItemKey,
          quantity: Number(payload.quantity || 1),
          properties,
        }),
      });
    }

    // Applies a cart-level discount code via the AJAX Cart API.
    // Only called when config.triggerDiscountCode is non-empty.
    async applyDiscountCode(code) {
      const trimmed = String(code || '').trim();
      if (!trimmed) return;

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: trimmed }),
      });

      if (!response.ok) {
        window.console.warn('[unlock-free-shipping-popup] Failed to apply discount code:', trimmed);
      }
    }

    handleProductLinkClick(event) {
      const link = event.target.closest('[data-unlock-product-link]');
      if (!link || !this.contains(link)) return;

      // Never navigate away from the theme editor preview.
      if (this.isPreview) {
        event.preventDefault();
        return;
      }

      const href = link.getAttribute('href');
      if (!href) return;

      // Intercept so we can guarantee the Mini Bloat OTP add persisted before leaving.
      event.preventDefault();
      this.navigateWithTrigger(href);
    }

    async navigateWithTrigger(href) {
      try {
        await this.ensureTriggerInCart();
      } catch (error) {
        window.console.error('[unlock-free-shipping-popup]', error);
      } finally {
        window.location.assign(href);
      }
    }

    async ensureTriggerInCart() {
      const triggerId = Number(this.config?.triggerProductId || 0);
      if (!triggerId) return;

      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((response) => response.json());

      const alreadyInCart = (cart.items || []).some(
        (item) => Number(item.product_id) === triggerId
      );
      if (alreadyInCart || !this.triggerVariantId) return;

      await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: Number(this.triggerVariantId),
          quantity: Number(this.triggerQuantity || 1),
          properties: this.getTriggerLineItemProperties(),
        }),
      });
    }

    closeCartDrawerIfOpen() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.close && typeof cartDrawer.close === 'function') {
        cartDrawer.close();
      }

      const drawerElement = document.getElementById('cart-drawer');
      if (drawerElement?.classList?.contains('active')) {
        drawerElement.classList.remove('active');
      }
    }

    goToSlide(index, animate = true) {
      if (!this.offers?.length || !this.track) return;

      const maxIndex = this.offers.length - 1;
      const nextIndex = Math.max(0, Math.min(index, maxIndex));
      this.setActiveIndex(nextIndex);

      // Native scroll-snap: each slide is full track width, so the offset is index * width.
      this.track.scrollTo({
        left: nextIndex * this.track.clientWidth,
        behavior: animate ? 'smooth' : 'auto',
      });
    }

    setActiveIndex(index) {
      this.activeIndex = index;
      this.activeOffer = this.offers[index];
      this.updateControls();
      this.updateSlideUI(this.activeOffer);
    }

    updateControls() {
      const maxIndex = this.offers.length - 1;

      if (this.prevButton) this.prevButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === maxIndex;

      this.dotsContainer?.querySelectorAll('[data-unlock-dot]').forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === this.activeIndex ? 'true' : 'false');
      });
    }

    // Keep dots/nav in sync while the user swipes the native scroller.
    handleTrackScroll() {
      if (this.scrollRaf || !this.track || !this.offers?.length) return;

      this.scrollRaf = window.requestAnimationFrame(() => {
        this.scrollRaf = null;
        const width = this.track.clientWidth || 1;
        const index = Math.round(this.track.scrollLeft / width);
        const clamped = Math.max(0, Math.min(index, this.offers.length - 1));
        if (clamped !== this.activeIndex) this.setActiveIndex(clamped);
      });
    }

    updateSlideUI(offer) {
      if (!offer?.slide || !offer.selectedVariant) return;

      const { price, compareAt } = this.getPricingForVariant(offer.selectedVariant);
      const compareEl = offer.slide.querySelector('[data-unlock-slide-compare]');
      const currentEl = offer.slide.querySelector('[data-unlock-slide-current]');

      if (currentEl) {
        currentEl.textContent = this.formatMoney(price);
      }

      if (compareEl) {
        compareEl.textContent = compareAt > 0 ? this.formatMoney(compareAt) : '';
        compareEl.hidden = compareAt <= 0;
      }
    }

    async handleAccept() {
      // In the theme editor we only preview the design — don't mutate the cart.
      if (this.isPreview) {
        this.closeModal({ openCart: false });
        return;
      }

      if (!this.activeOffer?.selectedVariant || this.isAdding) return;

      this.isAdding = true;
      this.acceptButton?.setAttribute('disabled', 'disabled');

      const variant = this.activeOffer.selectedVariant;
      const sellingPlanId = this.getSellingPlanIdForVariant(variant);
      const payload = {
        id: Number(variant.id),
        quantity: 1,
        properties: {
          _unlock_free_shipping_upsell: 'true',
        },
      };

      if (sellingPlanId) {
        payload.selling_plan = Number(sellingPlanId);
      }

      try {
        // Apply the discount before adding the product so it's active on the line item.
        const discountCode = String(this.config.triggerDiscountCode || '').trim();
        if (discountCode) {
          await this.applyDiscountCode(discountCode);
        }

        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          throw new Error('Unable to add subscription upsell.');
        }

        this.closeModal({ openCart: true });
      } catch (error) {
        window.console.error('[unlock-free-shipping-popup]', error);
      } finally {
        this.isAdding = false;
        this.acceptButton?.removeAttribute('disabled');
      }
    }

    openModal() {
      if (!this.modal) return;

      window.__pdpUpsellPopupOpen = true;
      this.previouslyFocused = document.activeElement;
      this.modal.setAttribute('open', '');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('pdp-upsell-popup-open', 'unlock-free-shipping-popup-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        if (typeof window.trapFocus === 'function') {
          window.trapFocus(this.card, this.acceptButton);
        } else {
          this.acceptButton?.focus();
        }
      });
    }

    openCartAfterPopup() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer({ open: true });
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.open && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    closeModal(options = {}) {
      if (!this.modal) return;
      const shouldOpenCart = options?.openCart === true && !this.isPreview;

      this.modal.removeAttribute('open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('pdp-upsell-popup-open', 'unlock-free-shipping-popup-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.handleKeydown);
      window.__pdpUpsellPopupOpen = false;
      window.__pdpUpsellDeferCartDrawer = false;

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.previouslyFocused);
      } else {
        this.previouslyFocused?.focus?.();
      }

      if (shouldOpenCart) {
        this.openCartAfterPopup();
      }
    }

    handleBackdropClick(event) {
      if (event.target === this.modal) {
        this.closeModal({ openCart: true });
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.stopPropagation();
        this.closeModal({ openCart: true });
      }
    }
  }

  customElements.define('unlock-free-shipping-popup', UnlockFreeShippingPopup);
}
